import cx from 'classnames';
import React from 'react';
import styles from './circular-progress.module.scss';

export enum Size {
    SMALL = 16,
    SMEDIUM = 20,
    MEDIUM = 24,
    LARGE = 32,
    XLARGE = 48,
}

/** Maps each Size to its proportional stroke width */
const STROKE_WIDTH_MAP: Record<Size, number> = {
    [Size.SMALL]: 4,
    [Size.SMEDIUM]: 5,
    [Size.MEDIUM]: 6,
    [Size.LARGE]: 8,
    [Size.XLARGE]: 12,
};

enum CircularProgressTestIds {
    PROGRESS_RING_BACKGROUND_ACTIVE = 'circular-progress-background-active',
    PROGRESS_RING_BACKGROUND_EMPTY = 'circular-progress-background-empty',
}

interface CircularProgressProps {
    steps: number;
    currentStep: number;
    size?: Size;
    className?: string;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
    steps,
    currentStep,
    size = Size.MEDIUM,
    className,
}) => {
    const strokeWidth = STROKE_WIDTH_MAP[size];

    // Make the circle smaller so the thick stroke doesn't overflow outside the container
    const radius = (size - strokeWidth) / 2;

    // How long is the circle's edge (like measuring a ribbon wrapped around it)
    const circumference = 2 * Math.PI * radius;

    // Figure out the progress as a percentage (0-100)
    // Clamp between 0 and 100 to handle negative currentStep or currentStep > steps
    const computedProgress =
        steps > 0 ? Math.min(Math.max((currentStep / steps) * 100, 0), 100) : 0;

    // How much of the circle to hide
    // At 0% progress, we hide the whole circle (offset = circumference)
    // At 100% progress, we show the whole circle (offset = 0)
    const offset = circumference - (computedProgress / 100) * circumference;
    const isEmpty = currentStep === 0;

    return (
        <div
            className={cx(styles.circularProgress, className)}
            style={{ width: size, height: size }}
            role="progressbar"
            aria-valuenow={currentStep}
            aria-valuemin={0}
            aria-valuemax={steps}
            aria-label="Progress bar"
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className={styles.progressRing}
            >
                {/* The gray/empty ring that's always visible behind */}
                <circle
                    className={cx(styles.progressRingBackground, {
                        [styles.progressRingBackgroundActive]: !isEmpty,
                        [styles.progressRingBackgroundEmpty]: isEmpty,
                    })}
                    strokeWidth={strokeWidth}
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                    data-testid={
                        isEmpty
                            ? CircularProgressTestIds.PROGRESS_RING_BACKGROUND_EMPTY
                            : CircularProgressTestIds.PROGRESS_RING_BACKGROUND_ACTIVE
                    }
                />
                {/* The colored ring on top that fills up as progress increases */}
                <circle
                    className={styles.progressRingProgress}
                    strokeWidth={strokeWidth}
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                    strokeDasharray={circumference} // Draw the circle as one long line
                    strokeDashoffset={offset} // Increase the offset to hide the portion of the circle
                />
            </svg>
        </div>
    );
};
